import { createCurrencyFormatter } from '@frontends/util-utils';
import { type DataFeed } from '../tradingView/lib/datafeed';

interface StepOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export const SPEED_OPTIONS = [
  { value: 5, label: '5x' },
  { value: 4, label: '4x' },
  { value: 3, label: '3x' },
  { value: 2, label: '2x' },
  { value: 1, label: '1x' },
  { value: 0.5, label: '0.5x' },
  { value: 0.3, label: '0.3x' },
  { value: 0.2, label: '0.2x' },
  { value: 0.1, label: '0.1x' },
  { value: 500, label: '>>' },
] as const;

const STEP_OPTIONS_BASE: ReadonlyArray<{ value: string; label: string }> = [
  { value: '1', label: '1m' },
  { value: '2', label: '2m' },
  { value: '3', label: '3m' },
  { value: '5', label: '5m' },
  { value: '10', label: '10m' },
  { value: '15', label: '15m' },
  { value: '30', label: '30m' },
  { value: '60', label: '1h' },
  { value: '120', label: '2h' },
  { value: '180', label: '3h' },
  { value: '240', label: '4h' },
  { value: '360', label: '6h' },
  { value: '480', label: '8h' },
  { value: '720', label: '12h' },
  { value: '1D', label: '1d' },
  { value: '1W', label: '1w' },
];

const BALANCE_VALUES = [100, 200, 300, 500, 1000, 5000, 10000, 100000, 1000000] as const;

export function buildBalanceOptions(accountBalance?: number, currency?: string): { value: number; label: string }[] {
  const formatted = createCurrencyFormatter(currency || 'USD', navigator.language, {
    maximumFractionDigits: 0,
  });

  const values = [...BALANCE_VALUES] as number[];

  if (accountBalance !== undefined && accountBalance > 0 && !values.includes(accountBalance)) {
    values.push(accountBalance);
    values.sort((a, b) => a - b);
  }

  return values.map((value) => ({ value, label: formatted.format(value) }));
}

/**
 * Build step options with `disabled` computed from chart resolution.
 * Step is allowed iff:
 *   - chartRes / step is an integer (i.e. chartRes % step === 0)
 *   - chartRes / step <= 24 (≤ 24 sub-steps per chart bar)
 */
export function buildStepOptions(chartRes: string, datafeed: DataFeed): ReadonlyArray<StepOption> {
  const chartSec = datafeed.getSecFromResolution(chartRes);

  if (!Number.isFinite(chartSec) || chartSec <= 0) {
    return STEP_OPTIONS_BASE.map((option) => ({ ...option, disabled: false }));
  }

  return STEP_OPTIONS_BASE.map((option) => {
    const stepSec = datafeed.getSecFromResolution(option.value);
    const allowed = Number.isFinite(stepSec) && stepSec > 0 && chartSec % stepSec === 0 && chartSec / stepSec <= 24;

    return { ...option, disabled: !allowed };
  });
}
